import { useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../api/firebase/references";
import { HealthCareFacilityModel } from "../data-model/healthFacility";

/**
 * Represents the state of healthcare facility data fetching.
 * @interface HealthcareFacilityData
 * @property {HealthCareFacilityModel[]} [data] - The array of healthcare facilities, undefined when not yet fetched
 * @property {boolean} loading - Indicates if the data is currently being fetched
 * @property {string | null} error - Error message if the fetch failed, null otherwise
 */
interface HealthcareFacilityData {
  data?: HealthCareFacilityModel[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch and manage healthcare facility data from Firestore.
 *
 * Automatically fetches all healthcare facilities on mount and provides
 * a memoized list of options suitable for dropdown components.
 *
 * @returns {Object} Hook return object
 * @returns {HealthcareFacilityData} returns.healthCareFacilities - Current state containing data, loading, and error
 * @returns {() => Promise<void>} returns.fetchAllHealthCareFacilities - Function to manually refetch all facilities
 * @returns {Array<{value: string, label: string}>} returns.healthCareFacilityOptions - Memoized array of options for dropdowns
 *
 * @example
 * // Basic usage
 * const { healthCareFacilities, healthCareFacilityOptions } = useHealthCareFacilities();
 *
 * if (healthCareFacilities.loading) return <Spinner />;
 * if (healthCareFacilities.error) return <Error message={healthCareFacilities.error} />;
 *
 * return <Select options={healthCareFacilityOptions} />;
 *
 * @example
 * // Manual refetch
 * const { fetchAllHealthCareFacilities } = useHealthCareFacilities();
 *
 * const handleRefresh = async () => {
 *   await fetchAllHealthCareFacilities();
 * };
 */
export const useHealthCareFacilities = () => {
  const [healthCareFacilities, setHealthCareFacilities] =
    useState<HealthcareFacilityData>({
      data: undefined,
      loading: false,
      error: null,
    });

  /**
   * Fetches all healthcare facilities from Firestore.
   * Sets loading state during fetch and handles errors appropriately.
   * @async
   * @returns {Promise<void>}
   */
  const fetchAllHealthCareFacilities = async () => {
    setHealthCareFacilities((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const healthCareFacilities =
        await FirestoreService.getAllDocuments<HealthCareFacilityModel>(
          FirestoreCollectionReference.healthcareFacility(),
        );
      setHealthCareFacilities((prev) => ({
        ...prev,
        data: healthCareFacilities,
      }));
    } catch (error) {
      setHealthCareFacilities((prev) => ({
        ...prev,
        error: error as string,
        data: undefined,
      }));
    } finally {
      setHealthCareFacilities((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const healthCareFacilityOptions = useMemo(
    () =>
      healthCareFacilities.data?.map((healthCareFacility) => ({
        value: healthCareFacility?.DocId ?? "",
        label: healthCareFacility?.Name ?? "",
      })) ?? [],
    [healthCareFacilities],
  );

  useEffect(() => {
    fetchAllHealthCareFacilities();
  }, []);

  return {
    healthCareFacilities,
    fetchAllHealthCareFacilities,
    healthCareFacilityOptions,
  };
};
